import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { RightDrawer } from './RightDrawer';
import { CountrySelect } from '../common/CountrySelect';
import { COUNTRIES as ALL_COUNTRIES, COUNTRY_BY_CODE } from '../../data/countries';
import { useLanguage } from '../../context/LanguageContext';
import {
 Check, Smartphone, FileText, IdCard, CreditCard, AlertTriangle,
 Upload, Camera, CheckCircle2, Loader2, X, ShieldCheck, Clock,
 Zap, Award, ArrowRight, QrCode, RefreshCw, Circle,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Country list: imported from shared data module above (full 200+ countries).
// We keep a local alias for the original `COUNTRIES.find(...)` calls within this file.
const COUNTRIES = ALL_COUNTRIES;

const DOC_TYPES = [
 { id: 'driver', ar_label: 'رخصة قيادة', en_label: "Driver's license", ar_caption: 'الوجه والظهر مطلوبان', en_caption: 'Front & back required', icon: CreditCard },
 { id: 'id_card', ar_label: 'بطاقة هوية وطنية', en_label: 'National ID card', ar_caption: 'الوجه والظهر مطلوبان', en_caption: 'Front & back required', icon: IdCard },
 { id: 'passport', ar_label: 'جواز سفر', en_label: 'Passport', ar_caption: 'صفحة بيانات الجواز فقط', en_caption: 'Photo page only', icon: FileText },
];

const BENEFITS = [
 { icon: Zap, ar_title: 'حدود أعلى', en_title: 'Higher limits', ar_body: 'سحوبات تصل إلى $10M وإيداعات أسرع.', en_body: 'Unlock withdrawals up to $10M and faster deposits.' },
 { icon: ShieldCheck, ar_title: 'ثقة تنظيمية', en_title: 'Regulatory trust', ar_body: 'التزام تلقائي بمعايير AML وKYC العالمية.', en_body: 'Comply with global AML & KYC standards automatically.' },
 { icon: Award, ar_title: 'خدمات مميّزة', en_title: 'Premium services', ar_body: 'فروقات VIP، نسخ صفقات، ودعم أولوي.', en_body: 'Access VIP spreads, copy trading, and priority support.' },
];

// ═══════════════════════════════════════════════════════
// STEP 1 — Intro
// ═══════════════════════════════════════════════════════
const StepIntro = ({ onStart, isRTL }) => (
 <div className="space-y-6">
 <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B0F14] via-[#111A26] to-[#050A12] text-white p-7">
 <div className="absolute -end-8 -top-8 w-40 h-40 rounded-full bg-[var(--gg-brand)]/20 blur-3xl" />
 <div className="relative">
 <div className="w-14 h-14 rounded-2xl bg-[var(--gg-brand)]/15 border border-[var(--gg-brand)]/30 flex items-center justify-center mb-4">
 <ShieldCheck className="w-7 h-7 text-[var(--gg-brand)]" strokeWidth={1.8} />
 </div>
 <h3 className="text-[22px] font-semibold tracking-tight leading-tight mb-2">{isRTL ? 'وثّق هويتك خلال دقيقتين' : 'Verify your identity in 2 minutes'}</h3>
 <p className="text-[13.5px] text-white/60 leading-relaxed">
 {isRTL ? 'عملية آمنة وسريعة لتفعيل كامل صلاحيات حسابك للتداول.' : 'A quick, secure process to unlock the full power of your trading account.'}
 </p>
 <div className="flex items-center gap-4 mt-5 pt-5 border-t border-white/10">
 <div className="flex items-center gap-1.5 text-[12px] text-white/70">
 <Clock className="w-[13px] h-[13px] text-[var(--gg-brand)]" />
 <span>{isRTL ? '~دقيقتان' : '~2 minutes'}</span>
 </div>
 <span className="w-px h-3 bg-white/20" />
 <div className="flex items-center gap-1.5 text-[12px] text-white/70">
 <ShieldCheck className="w-[13px] h-[13px] text-[var(--gg-brand)]" />
 <span>{isRTL ? 'تشفير بمستوى بنكي' : 'Bank-grade encryption'}</span>
 </div>
 </div>
 </div>
 </div>

 <div>
 <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gg-muted-2)] mb-3">
 {isRTL ? 'ما الذي ستفتحه' : "What you'll unlock"}
 </div>
 <div className="space-y-2.5">
 {BENEFITS.map((b, i) => (
 <div key={i} className="flex items-start gap-3.5 p-3.5 rounded-xl border border-[var(--gg-hair)] bg-white">
 <div className="w-9 h-9 rounded-lg bg-[var(--gg-bg)] flex items-center justify-center shrink-0">
 <b.icon className="w-[17px] h-[17px] text-[var(--gg-ink)]" strokeWidth={1.8} />
 </div>
 <div className="min-w-0">
 <div className="text-[13.5px] font-semibold text-[var(--gg-ink)] leading-tight mb-0.5">{isRTL ? b.ar_title : b.en_title}</div>
 <div className="text-[12px] text-[var(--gg-muted)] leading-relaxed">{isRTL ? b.ar_body : b.en_body}</div>
 </div>
 </div>
 ))}
 </div>
 </div>

 <div>
 <div className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[var(--gg-muted-2)] mb-3">
 {isRTL ? 'ما تحتاج إليه' : "What you'll need"}
 </div>
 <div className="grid grid-cols-2 gap-2.5">
 {[
 { icon: FileText, label: isRTL ? 'وثيقة هوية حكومية صالحة' : 'A valid gov. ID' },
 { icon: Camera, label: isRTL ? 'كاميرا الهاتف / الكمبيوتر' : 'Your camera / phone' },
 ].map((item, i) => (
 <div key={i} className="p-3.5 rounded-xl border border-[var(--gg-hair)] bg-[var(--gg-bg)]">
 <item.icon className="w-[18px] h-[18px] text-[var(--gg-muted)] mb-2" strokeWidth={1.8} />
 <div className="text-[12.5px] font-medium text-[var(--gg-ink)] leading-tight">{item.label}</div>
 </div>
 ))}
 </div>
 </div>
 </div>
);

// ═══════════════════════════════════════════════════════
// STEP 2 — Country + Doc type
// ═══════════════════════════════════════════════════════
const StepDocType = ({ country, setCountry, docType, setDocType, isRTL }) => {
 return (
 <div className="space-y-6">
 <div className="p-4 rounded-xl bg-[var(--gg-bg)] border border-[var(--gg-hair)]">
 <div className="flex items-start gap-3">
 <Circle className="w-4 h-4 text-[var(--gg-brand)] mt-0.5 fill-[var(--gg-brand)]" />
 <div className="text-[12px] text-[var(--gg-muted)] leading-relaxed">
 {isRTL
   ? 'اختر الدولة المُصدِرة للوثيقة، ثم نوعها. سنوجّه التحقق إلى فريق الامتثال المناسب.'
   : "Choose the country that issued your document, then the type. We'll use this to route your verification to the right compliance team."}
 </div>
 </div>
 </div>

 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--gg-muted-2)] mb-2 block">
 {isRTL ? 'الدولة المُصدِرة *' : 'Issuing country *'}
 </label>
 <CountrySelect
   value={country}
   onChange={setCountry}
   isRTL={isRTL}
   testId="verify-country"
 />
 </div>

 <div>
 <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--gg-muted-2)] mb-2 block">
 {isRTL ? 'نوع الوثيقة *' : 'Document type *'}
 </label>
 <div className="flex flex-col gap-2">
 {DOC_TYPES.map(d => {
 const Icon = d.icon;
 const active = docType === d.id;
 return (
 <button
 key={d.id}
 onClick={() => setDocType(d.id)}
 className={`px-4 py-3.5 rounded-xl border flex items-center gap-3.5 transition text-left ${
 active
 ? 'border-[var(--gg-ink)] bg-[var(--gg-ink)] text-white'
 : 'border-[var(--gg-hair)] hover:border-[var(--gg-line)] bg-white text-[var(--gg-ink)]'
 }`}
 data-testid={`verify-doc-${d.id}`}
 >
 <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
 active ? 'bg-white/10' : 'bg-[var(--gg-bg)]'
 }`}>
 <Icon className={`w-[18px] h-[18px] ${active ? 'text-white' : 'text-[var(--gg-muted)]'}`} />
 </div>
 <div className="flex-1">
 <div className="text-[14px] font-semibold leading-tight mb-0.5">{isRTL ? d.ar_label : d.en_label}</div>
 <div className={`text-[11.5px] leading-tight ${active ? 'text-white/60' : 'text-[var(--gg-muted-2)]'}`}>
 {isRTL ? d.ar_caption : d.en_caption}
 </div>
 </div>
 <span className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${active ? 'border-white' : 'border-[var(--gg-line)]'}`}>
 {active && <span className="w-2.5 h-2.5 rounded-full bg-white" />}
 </span>
 </button>
 );
 })}
 </div>
 </div>
 </div>
 );
};

// ═══════════════════════════════════════════════════════
// STEP 3 — Upload document
// ═══════════════════════════════════════════════════════
const UploadSlot = ({ label, hint, file, onChange, testId, isRTL = false }) => {
 const inputRef = useRef();
 const [preview, setPreview] = useState(null);
 const pick = (f) => {
 onChange(f);
 if (f) {
 const reader = new FileReader();
 reader.onload = e => setPreview(e.target.result);
 reader.readAsDataURL(f);
 } else setPreview(null);
 };
 return (
 <div>
 <div className="flex items-baseline justify-between mb-2">
 <label className="text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--gg-muted-2)]">{label}</label>
 {hint && <span className="text-[11px] text-[var(--gg-muted-2)]">{hint}</span>}
 </div>
 <input
 ref={inputRef} type="file" accept="image/*,application/pdf" className="hidden"
 onChange={e => pick(e.target.files?.[0])}
 data-testid={`${testId}-input`}
 />
 {preview ? (
 <div className="relative rounded-xl border border-[var(--gg-hair)] overflow-hidden bg-[var(--gg-bg)] h-44 group">
 <img src={preview} alt="" className="w-full h-full object-cover" />
 <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition" />
 <button
 onClick={() => { pick(null); if (inputRef.current) inputRef.current.value=''; }}
 className="absolute top-2 end-2 w-8 h-8 rounded-full bg-white/95 shadow flex items-center justify-center text-[var(--gg-ink)] hover:bg-white"
 data-testid={`${testId}-remove`}
 >
 <X className="w-4 h-4" />
 </button>
 <div className="absolute bottom-2 start-2 end-10 flex items-center gap-1.5">
 <CheckCircle2 className="w-3.5 h-3.5 text-[var(--gg-brand)]" />
 <span className="text-[11px] text-white font-medium truncate">
 {file?.name?.slice(0, 28)}{file?.name?.length > 28 ? '…' : ''}
 </span>
 </div>
 </div>
 ) : (
 <button
 onClick={() => inputRef.current?.click()}
 className="w-full h-44 rounded-xl border-2 border-dashed border-[var(--gg-hair)] hover:border-[var(--gg-ink)] bg-gradient-to-br from-[var(--gg-bg)] to-white flex flex-col items-center justify-center gap-2 transition group"
 data-testid={`${testId}-dropzone`}
 >
 <div className="w-11 h-11 rounded-full bg-white shadow-[var(--gg-shadow-sm)] flex items-center justify-center group-hover:scale-110 transition-transform">
 <Upload className="w-[18px] h-[18px] text-[var(--gg-ink)]" />
 </div>
 <div className="text-[13.5px] font-semibold text-[var(--gg-ink)]">{isRTL ? 'اضغط للرفع' : 'Click to upload'}</div>
 <div className="text-[11px] text-[var(--gg-muted-2)]">{isRTL ? 'PNG · JPG · PDF · حتى 10 ميجابايت' : 'PNG · JPG · PDF · up to 10 MB'}</div>
 </button>
 )}
 </div>
 );
};

const StepUpload = ({ docType, frontFile, setFront, backFile, setBack, onSwitchToPhone, isRTL }) => {
 const needsBack = docType !== 'passport';
 const docMeta = DOC_TYPES.find(d => d.id === docType);
 const docLabel = (isRTL ? docMeta?.ar_label : docMeta?.en_label) || 'document';

 return (
 <div className="space-y-5">
 <div className="flex items-start gap-3 p-4 rounded-xl bg-[var(--gg-bg)] border border-[var(--gg-hair)]">
 <Camera className="w-4 h-4 text-[var(--gg-brand)] mt-0.5 shrink-0" />
 <div className="text-[12px] text-[var(--gg-muted)] leading-relaxed">
 <div className="font-semibold text-[var(--gg-ink)] mb-0.5">{isRTL ? 'نصائح للحصول على صورة واضحة' : 'Tips for a clear photo'}</div>
 {isRTL ? 'الأركان الأربعة ظاهرة · بدون لمعان أو ضبابية · الوثيقة على سطح داكن مستوٍ.' : 'All four corners visible · no glare or blur · document flat on a dark surface.'}
 </div>
 </div>

 <UploadSlot
 label={needsBack ? `${docLabel} — ${isRTL ? 'الوجه' : 'Front'} *` : `${docLabel} *`}
 hint={needsBack ? (isRTL ? 'وجه الصورة' : 'Photo side') : (isRTL ? 'الصفحة الرئيسية' : 'Main page')}
 file={frontFile} onChange={setFront} testId="verify-front" isRTL={isRTL}
 />
 {needsBack && (
 <UploadSlot label={`${docLabel} — ${isRTL ? 'الظهر' : 'Back'} *`} hint={isRTL ? 'جهة الباركود / MRZ' : 'Barcode / MRZ side'} file={backFile} onChange={setBack} testId="verify-back" isRTL={isRTL} />
 )}

 <div className="relative flex items-center py-1">
 <span className="flex-1 h-px bg-[var(--gg-hair)]" />
 <span className="px-3 text-[11px] uppercase tracking-[0.1em] text-[var(--gg-muted-2)] font-semibold">{isRTL ? 'أو' : 'or'}</span>
 <span className="flex-1 h-px bg-[var(--gg-hair)]" />
 </div>

 <button
 onClick={onSwitchToPhone}
 className="w-full p-4 rounded-xl border border-[var(--gg-hair)] bg-white hover:border-[var(--gg-ink)] transition flex items-center gap-3.5 text-left group"
 data-testid="verify-continue-phone"
 >
 <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D85308] to-[#BC3F05] flex items-center justify-center shrink-0">
 <Smartphone className="w-[18px] h-[18px] text-[var(--gg-ink)]" strokeWidth={2} />
 </div>
 <div className="flex-1">
 <div className="text-[13.5px] font-semibold text-[var(--gg-ink)] mb-0.5">{isRTL ? 'أكمل من هاتفك' : 'Continue on your phone'}</div>
 <div className="text-[11.5px] text-[var(--gg-muted)]">{isRTL ? 'امسح رمز QR، التقط الصور بهاتفك، وسنزامن كل شيء تلقائياً.' : "Scan a QR code, snap photos on mobile — we'll sync back here automatically."}</div>
 </div>
 <ArrowRight className="w-[18px] h-[18px] text-[var(--gg-muted-2)] group-hover:text-[var(--gg-ink)] group-hover:translate-x-0.5 transition" />
 </button>
 </div>
 );
};

// ═══════════════════════════════════════════════════════
// STEP 4 — Selfie (take here OR phone)
// ═══════════════════════════════════════════════════════
const StepSelfie = ({ onDone, onSwitchToPhone, isRTL = false }) => {
 const [photo, setPhoto] = useState(null);
 const [submitting, setSubmitting] = useState(false);
 const videoRef = useRef(null);
 const canvasRef = useRef(null);
 const [camOn, setCamOn] = useState(false);
 const [camErr, setCamErr] = useState('');

 const startCam = async () => {
 setCamErr('');
 try {
 const stream = await navigator.mediaDevices.getUserMedia({
 video: { facingMode: 'user', width: { ideal: 720 }, height: { ideal: 960 } },
 audio: false,
 });
 if (videoRef.current) {
 videoRef.current.srcObject = stream;
 await videoRef.current.play();
 setCamOn(true);
 }
 } catch { setCamErr(isRTL ? 'تم رفض إذن الوصول للكاميرا.' : 'Camera permission denied.'); }
 };

 const shoot = () => {
 const v = videoRef.current, c = canvasRef.current;
 if (!v || !c) return;
 c.width = v.videoWidth; c.height = v.videoHeight;
 const ctx = c.getContext('2d');
 ctx.save(); ctx.translate(c.width, 0); ctx.scale(-1, 1);
 ctx.drawImage(v, 0, 0, c.width, c.height);
 ctx.restore();
 c.toBlob(blob => {
 const file = new File([blob], 'selfie.jpg', { type: 'image/jpeg' });
 setPhoto({ data: c.toDataURL('image/jpeg', 0.92), file });
 v.srcObject?.getTracks().forEach(t => t.stop());
 setCamOn(false);
 }, 'image/jpeg', 0.92);
 };

 const submit = async () => {
 if (!photo?.file) return;
 setSubmitting(true);
 try {
 const authToken = localStorage.getItem('token');
 const fd = new FormData();
 fd.append('file', photo.file);
 await axios.post(`${API}/api/kyc/upload-selfie`, fd, {
 headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'multipart/form-data' },
 }).catch(() => {});
 } catch {}
 setSubmitting(false);
 onDone();
 };

 return (
 <div className="space-y-5">
 <div className="rounded-2xl overflow-hidden bg-gradient-to-br from-[#0B0F14] to-[#1D232A] p-6 text-white">
 <div className="flex items-center gap-2 mb-1.5">
 <Camera className="w-[14px] h-[14px] text-[var(--gg-brand)]" />
 <span className="text-[11px] uppercase tracking-[0.1em] font-semibold text-[var(--gg-brand)]">{isRTL ? 'الخطوة الأخيرة' : 'Final step'}</span>
 </div>
 <h3 className="text-[18px] font-semibold tracking-tight mb-1">{isRTL ? 'التقط صورة سيلفي' : 'Take a selfie'}</h3>
 <p className="text-[12.5px] text-white/60 leading-relaxed">
 {isRTL ? 'سنطابق وجهك مع الوثيقة للتأكد أنك أنت. انظر مباشرة إلى الكاميرا.' : "We'll match your face with the ID to confirm it's really you. Look straight at the camera."}
 </p>
 </div>

 <canvas ref={canvasRef} className="hidden" />

 {photo ? (
 <div className="relative rounded-2xl overflow-hidden bg-[var(--gg-bg)] aspect-square">
 <img src={photo.data} alt="" className="w-full h-full object-cover" />
 <button
 onClick={() => setPhoto(null)}
 className="absolute top-3 end-3 w-9 h-9 rounded-full bg-white/95 shadow flex items-center justify-center text-[var(--gg-ink)]"
 data-testid="verify-selfie-retake"
 >
 <RefreshCw className="w-4 h-4" />
 </button>
 </div>
 ) : camOn ? (
 <div className="relative rounded-2xl overflow-hidden bg-black aspect-square">
 <video
 ref={videoRef}
 playsInline muted autoPlay
 className="absolute inset-0 w-full h-full object-cover"
 style={{ transform: 'scaleX(-1)' }}
 />
 <button
 onClick={shoot}
 className="absolute bottom-4 left-1/2 -translate-x-1/2 w-16 h-16 rounded-full bg-white shadow-lg flex items-center justify-center"
 data-testid="verify-selfie-shoot"
 >
 <div className="w-12 h-12 rounded-full border-4 border-[var(--gg-ink)]" />
 </button>
 </div>
 ) : (
 <button
 onClick={startCam}
 className="w-full aspect-square rounded-2xl border-2 border-dashed border-[var(--gg-hair)] hover:border-[var(--gg-ink)] bg-gradient-to-br from-[var(--gg-bg)] to-white flex flex-col items-center justify-center gap-3 transition"
 data-testid="verify-selfie-capture"
 >
 <div className="w-16 h-16 rounded-full bg-white shadow-[var(--gg-shadow-md)] flex items-center justify-center">
 <Camera className="w-7 h-7 text-[var(--gg-ink)]" strokeWidth={1.8} />
 </div>
 <div className="text-[14px] font-semibold text-[var(--gg-ink)]">{isRTL ? 'فتح الكاميرا' : 'Open camera'}</div>
 {camErr && <div className="text-[11px] text-[var(--gg-danger)]">{camErr}</div>}
 </button>
 )}

 {photo && (
 <button
 onClick={submit}
 disabled={submitting}
 className="w-full h-12 gg-btn-primary text-[15px] disabled:opacity-40 inline-flex items-center justify-center gap-2"
 data-testid="verify-selfie-submit"
 >
 {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
 {submitting ? (isRTL ? 'جاري الإرسال…' : 'Submitting…') : (isRTL ? 'تأكيد وإرسال' : 'Confirm & Submit')}
 </button>
 )}

 <div className="relative flex items-center py-1">
 <span className="flex-1 h-px bg-[var(--gg-hair)]" />
 <span className="px-3 text-[11px] uppercase tracking-[0.1em] text-[var(--gg-muted-2)] font-semibold">{isRTL ? 'أو' : 'or'}</span>
 <span className="flex-1 h-px bg-[var(--gg-hair)]" />
 </div>

 <button
 onClick={onSwitchToPhone}
 className="w-full p-4 rounded-xl border border-[var(--gg-hair)] bg-white hover:border-[var(--gg-ink)] transition flex items-center gap-3.5 text-left group"
 data-testid="verify-selfie-phone"
 >
 <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#D85308] to-[#BC3F05] flex items-center justify-center shrink-0">
 <Smartphone className="w-[18px] h-[18px] text-[var(--gg-ink)]" strokeWidth={2} />
 </div>
 <div className="flex-1">
 <div className="text-[13.5px] font-semibold text-[var(--gg-ink)] mb-0.5">{isRTL ? 'استخدم كاميرا الهاتف بدلاً من ذلك' : 'Use your phone camera instead'}</div>
 <div className="text-[11.5px] text-[var(--gg-muted)]">{isRTL ? 'إضاءة وجودة أفضل على معظم الهواتف.' : 'Better lighting & quality on most phones.'}</div>
 </div>
 <ArrowRight className="w-[18px] h-[18px] text-[var(--gg-muted-2)] group-hover:text-[var(--gg-ink)] group-hover:translate-x-0.5 transition" />
 </button>
 </div>
 );
};

// ═══════════════════════════════════════════════════════
// STEP QR — Continue on phone
// ═══════════════════════════════════════════════════════
const StepPhoneQR = ({ phoneToken, onDetected, onBack, isRTL = false }) => {
 const [status, setStatus] = useState('waiting'); // waiting | scanning | uploaded

 // Poll phone-session status every 2s
 useEffect(() => {
 if (!phoneToken) return;
 const interval = setInterval(async () => {
 try {
 const r = await axios.get(`${API}/api/kyc/phone-session/${phoneToken}`);
 if (r.data?.selfie_uploaded) {
 setStatus('uploaded');
 clearInterval(interval);
 setTimeout(onDetected, 900);
 }
 } catch { /* noop */ }
 }, 2000);
 return () => clearInterval(interval);
 }, [phoneToken, onDetected]);

 const mobileUrl = phoneToken ? `${window.location.origin}/m/verify/${phoneToken}` : '';

 return (
 <div className="space-y-5">
 <div className="rounded-2xl bg-gradient-to-br from-[#D85308]/10 to-transparent border border-[var(--gg-brand)]/20 p-5 flex items-start gap-3">
 <div className="w-9 h-9 rounded-lg bg-[var(--gg-brand)]/20 flex items-center justify-center shrink-0">
 <Smartphone className="w-[17px] h-[17px] text-[var(--gg-brand-hover)]" />
 </div>
 <div>
 <div className="text-[13.5px] font-semibold text-[var(--gg-ink)] mb-0.5">{isRTL ? 'افتح على هاتفك' : 'Open on your phone'}</div>
 <div className="text-[12px] text-[var(--gg-muted)] leading-relaxed">
 {isRTL ? 'امسح رمز QR بكاميرا هاتفك — ستفتح صفحة آمنة لالتقاط صورة سيلفي. أبقِ هذه النافذة مفتوحة؛ سيتم التحديث تلقائياً.' : "Scan the QR code with your phone's camera — a secure page will open to take your selfie. Keep this window open; it will refresh automatically."}
 </div>
 </div>
 </div>

 {phoneToken ? (
 <>
 <div className="flex justify-center">
 <div className="relative p-5 rounded-2xl bg-white border border-[var(--gg-hair)] shadow-[var(--gg-shadow-sm)]">
 <QRCodeSVG
 value={mobileUrl}
 size={220}
 level="M"
 includeMargin={false}
 fgColor="#0B0F14"
 bgColor="#FFFFFF"
 imageSettings={{
 src: 'data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="10" fill="%2300DC82"/><text x="50%" y="55%" font-family="Arial,sans-serif" font-weight="700" font-size="22" text-anchor="middle" fill="%230B0F14">G</text></svg>',
 height: 40,
 width: 40,
 excavate: true,
 }}
 />
 {status === 'uploaded' && (
 <div className="absolute inset-0 rounded-2xl bg-[var(--gg-brand)]/95 flex flex-col items-center justify-center text-[var(--gg-ink)] gg-fade-up">
 <CheckCircle2 className="w-14 h-14 mb-2" strokeWidth={2.4} />
 <div className="text-[14px] font-bold">{isRTL ? 'تم الاستلام من الهاتف!' : 'Received from phone!'}</div>
 </div>
 )}
 </div>
 </div>

 <div className="flex items-center justify-center gap-2 text-[12px] text-[var(--gg-muted)]">
 <span className="relative flex h-2 w-2">
 <span className={`absolute inline-flex h-full w-full rounded-full ${status === 'uploaded' ? 'bg-[var(--gg-brand)]' : 'bg-amber-400'} opacity-75 animate-ping`} />
 <span className={`relative inline-flex rounded-full h-2 w-2 ${status === 'uploaded' ? 'bg-[var(--gg-brand)]' : 'bg-amber-500'}`} />
 </span>
 {status === 'uploaded' ? (isRTL ? 'تم الرفع — جاري الإنهاء…' : 'Uploaded — finalizing…') : (isRTL ? 'في انتظار هاتفك…' : 'Waiting for your phone…')}
 </div>

 <div className="p-3.5 rounded-xl bg-[var(--gg-bg)] border border-[var(--gg-hair)]">
 <div className="text-[11px] uppercase tracking-[0.06em] text-[var(--gg-muted-2)] font-semibold mb-1">{isRTL ? 'أو افتح هذا الرابط' : 'Or open this link'}</div>
 <div className="text-[11.5px] text-[var(--gg-ink)] font-geist-mono break-all leading-relaxed">{mobileUrl}</div>
 </div>
 </>
 ) : (
 <div className="py-12 flex items-center justify-center">
 <Loader2 className="w-6 h-6 animate-spin text-[var(--gg-muted)]" />
 </div>
 )}

 <button
 onClick={onBack}
 className="w-full h-11 text-[13.5px] text-[var(--gg-muted)] hover:text-[var(--gg-ink)] transition"
 data-testid="verify-phone-back"
 >
 ← Take photos on this device instead
 </button>
 </div>
 );
};

// ═══════════════════════════════════════════════════════
// STEP SUCCESS
// ═══════════════════════════════════════════════════════
const StepSuccess = ({ onClose, isRTL = false }) => (
 <div className="flex flex-col items-center text-center py-8 gg-fade-up">
 <div className="relative w-24 h-24 mb-5">
 <div className="absolute inset-0 rounded-full bg-[var(--gg-brand)]/15" />
 <div className="absolute inset-3 rounded-full bg-[var(--gg-brand)]/25" />
 <div className="absolute inset-6 rounded-full bg-[var(--gg-brand)] flex items-center justify-center">
 <CheckCircle2 className="w-8 h-8 text-white" strokeWidth={2.4} />
 </div>
 </div>
 <h3 className="text-[22px] font-semibold tracking-tight mb-2">{isRTL ? 'تم بنجاح!' : "You're all set"}</h3>
 <p className="text-[13.5px] text-[var(--gg-muted)] leading-relaxed max-w-[340px] mb-6">
 {isRTL ? 'فريق الامتثال يراجع طلبك الآن. تتم معظم عمليات التحقق خلال 5–15 دقيقة. سنخبرك فور الموافقة.' : "Our compliance team is reviewing your submission. Most verifications clear within 5–15 minutes. We'll notify you the moment it's approved."}
 </p>
 <div className="w-full bg-[var(--gg-bg)] rounded-xl px-4 py-3 mb-5 text-left border border-[var(--gg-hair)]">
 <div className="flex items-center justify-between">
 <div>
 <div className="text-[10px] uppercase tracking-[0.06em] text-[var(--gg-muted-2)] font-semibold">{isRTL ? 'الحالة' : 'Status'}</div>
 <div className="text-[14px] font-semibold text-[var(--gg-ink)] mt-0.5">{isRTL ? 'قيد المراجعة' : 'Under review'}</div>
 </div>
 <span className="relative flex h-2.5 w-2.5">
 <span className="absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75 animate-ping" />
 <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-amber-500" />
 </span>
 </div>
 </div>
 <button onClick={onClose} className="w-full h-12 gg-btn-primary text-[15px]" data-testid="verify-done">
 {isRTL ? 'تم' : 'Done'}
 </button>
 </div>
);

// ═══════════════════════════════════════════════════════
// MAIN DRAWER
// ═══════════════════════════════════════════════════════
export const VerifyIdentityDrawer = ({ open, onClose }) => {
 const { t, isRTL } = useLanguage();
 const [step, setStep] = useState(0); // 0 intro | 1 doc-type | 2 upload | 3 selfie | 4 phone-qr | 5 success
 const [country, setCountry] = useState('PS');
 const [docType, setDocType] = useState('passport');
 const [front, setFront] = useState(null);
 const [back, setBack] = useState(null);
 const [kycId, setKycId] = useState(null);
 const [phoneToken, setPhoneToken] = useState(null);
 const [submitting, setSubmitting] = useState(false);
 const [confirmExit, setConfirmExit] = useState(false);

 const needsBack = docType !== 'passport';

 const reset = () => {
 setStep(0); setCountry('PS'); setDocType('passport');
 setFront(null); setBack(null); setKycId(null); setPhoneToken(null);
 setSubmitting(false); setConfirmExit(false);
 };

 const close = () => {
 if (step === 5 || step === 0) { reset(); onClose?.(); return; }
 if (front || back || kycId) setConfirmExit(true);
 else { reset(); onClose?.(); }
 };

 const createPhoneSession = async () => {
 try {
 const token = localStorage.getItem('token');
 // Ensure we have a kyc_id — if user didn't upload, create a minimal doc
 if (!kycId) {
 const fd = new FormData();
 fd.append('document_type', docType);
 fd.append('nationality', country);
 if (front) fd.append('file', front);
 else {
 const blob = new Blob(['placeholder'], { type: 'image/jpeg' });
 fd.append('file', blob, 'pending.jpg');
 }
 const r = await axios.post(`${API}/api/kyc/upload`, fd, {
 headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
 });
 setKycId(r.data?.id);
 }
 const s = await axios.post(`${API}/api/kyc/phone-session`, {}, {
 headers: { Authorization: `Bearer ${token}` },
 });
 setPhoneToken(s.data.token);
 setStep(4);
 } catch (e) {
 // fallback — still show QR with placeholder
 setPhoneToken('unavailable-' + Date.now());
 setStep(4);
 }
 };

 const uploadDocs = async () => {
 setSubmitting(true);
 try {
 const token = localStorage.getItem('token');
 const fd = new FormData();
 fd.append('document_type', docType);
 fd.append('nationality', country);
 fd.append('file', front);
 const r = await axios.post(`${API}/api/kyc/upload`, fd, {
 headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
 });
 setKycId(r.data?.id);
 } catch (e) { /* keep flow going */ }
 setSubmitting(false);
 setStep(3);
 };

 const titleMap = {
 0: t('dashboard.verify.titles.intro'),
 1: t('dashboard.verify.titles.country'),
 2: t('dashboard.verify.titles.upload'),
 3: t('dashboard.verify.titles.selfie'),
 4: t('dashboard.verify.titles.phone'),
 5: '',
 };

 const subtitleMap = {
 0: t('dashboard.verify.subtitles.intro'),
 1: t('dashboard.verify.subtitles.country'),
 2: t('dashboard.verify.subtitles.upload', {
 doc: DOC_TYPES.find(d => d.id === docType)?.label || '',
 country: COUNTRIES.find(c => c.code === country)?.name || '',
 }),
 3: t('dashboard.verify.subtitles.selfie'),
 4: t('dashboard.verify.subtitles.phone'),
 5: '',
 };

 const canContinue =
 step === 0 ? true :
 step === 1 ? Boolean(country && docType) :
 step === 2 ? Boolean(front && (!needsBack || back)) :
 step === 3 ? false : // selfie step has its own submit
 false;

 const continueLabel =
 step === 0 ? t('dashboard.verify.buttons.getStarted') :
 step === 1 ? t('dashboard.verify.buttons.continue') :
 step === 2 ? (submitting ? t('dashboard.verify.buttons.uploading') : t('dashboard.verify.buttons.uploadContinue')) : '';

 const next = () => {
 if (step === 0) setStep(1);
 else if (step === 1) setStep(2);
 else if (step === 2) uploadDocs();
 };

 return (
 <>
 <RightDrawer
 open={open && !confirmExit}
 onClose={close}
 onBack={step > 0 && step < 5 ? () => setStep(step === 4 ? 2 : step - 1) : null}
 step={step > 0 && step < 5 ? t('dashboard.verify.step', { current: Math.min(step, 3), total: 3 }) : null}
 title={titleMap[step]}
 subtitle={subtitleMap[step]}
 testId="verify-identity-drawer"
 footer={
 step === 0 || step === 1 || step === 2
 ? (
 <button
 onClick={next}
 disabled={!canContinue || submitting}
 className="w-full h-12 gg-btn-primary text-[15px] disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
 data-testid="verify-continue"
 >
 {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
 {continueLabel}
 </button>
 )
 : null
 }
 >
 {/* Progress bar */}
 {step > 0 && step < 5 && (
 <div className="flex items-center gap-2 mb-6">
 {[1, 2, 3].map(s => (
 <div key={s} className={`flex-1 h-1 rounded-full transition-colors ${
 (step >= s && step !== 4) || (step === 4 && s <= 3) ? 'bg-[var(--gg-ink)]' : 'bg-[var(--gg-hair)]'
 }`} />
 ))}
 </div>
 )}

 {step === 0 && <StepIntro onStart={() => setStep(1)} isRTL={isRTL} />}
 {step === 1 && <StepDocType country={country} setCountry={setCountry} docType={docType} setDocType={setDocType} isRTL={isRTL} />}
 {step === 2 && <StepUpload docType={docType} frontFile={front} setFront={setFront} backFile={back} setBack={setBack} onSwitchToPhone={createPhoneSession} isRTL={isRTL} />}
 {step === 3 && <StepSelfie onDone={() => setStep(5)} onSwitchToPhone={createPhoneSession} isRTL={isRTL} />}
 {step === 4 && <StepPhoneQR phoneToken={phoneToken} onDetected={() => setStep(5)} onBack={() => setStep(2)} isRTL={isRTL} />}
 {step === 5 && <StepSuccess onClose={() => { reset(); onClose?.(); }} isRTL={isRTL} />}
 </RightDrawer>

 {confirmExit && (
 <>
 <div className="gg-drawer-overlay open" onClick={() => setConfirmExit(false)} />
 <div className="fixed inset-0 z-[62] flex items-center justify-center p-4 pointer-events-none">
 <div className="pointer-events-auto bg-white rounded-2xl shadow-[var(--gg-shadow-lg)] max-w-[420px] w-full p-6 gg-fade-up" data-testid="verify-exit-modal">
 <div className="w-12 h-12 rounded-full bg-[var(--gg-danger-bg)] flex items-center justify-center mb-4">
 <AlertTriangle className="w-5 h-5 text-[var(--gg-danger)]" />
 </div>
 <h3 className="text-[20px] font-semibold tracking-tight mb-1.5">{t('dashboard.verify.exit.title')}</h3>
 <p className="text-[14px] text-[var(--gg-muted)] leading-relaxed mb-6">
 {t('dashboard.verify.exit.body')}
 </p>
 <div className="flex flex-col gap-2">
 <button
 onClick={() => setConfirmExit(false)}
 className="w-full h-12 gg-btn-primary text-[15px]"
 data-testid="verify-exit-continue"
 >
 {t('dashboard.verify.exit.keepGoing')}
 </button>
 <button
 onClick={() => { reset(); onClose?.(); }}
 className="w-full h-12 text-[14px] text-[var(--gg-muted)] hover:text-[var(--gg-ink)] transition"
 data-testid="verify-exit-confirm"
 >
 {t('dashboard.verify.exit.save')}
 </button>
 </div>
 </div>
 </div>
 </>
 )}
 </>
 );
};

export default VerifyIdentityDrawer;
